import React, { useCallback, useRef } from 'react';
import * as S from './ChannelVideosList.styles';
import { ChannelVideo } from '@/typings/channelVideo';
import LandingPageBanner from '@/components/LandingPageBanner/LandingPageBanner';
import ChannelVideoCard from '@/components/ChannelVideoCard/ChannelVideoCard';
import { Variants, motion } from 'framer-motion';
import { VideoCategory } from '@/typings/videoCategory';
import { Box, Flex, Grid, Text, Title, useMantineTheme } from '@mantine/core';
import { ChannelVideoCarousel, ChannelVideoCarouselSlide } from '../Home/Home.styles';
import { useMediaQuery } from '@mantine/hooks';

type ChannelVideoListProps = {
  channelVideos: ChannelVideo[];
  categories: VideoCategory[];
};

const ChannelVideoList: React.FC<ChannelVideoListProps> = ({ channelVideos, categories }) => {
  const scrollRef = useRef(null);
  const itemMotion: Variants = {
    hidden: { opacity: 0, y: 50 },
    show: {
      transition: {
        mass: 1,
        duration: 0.6,
        type: 'tween',
      },
      opacity: [0, 0.2, 0.3, 0.5, 1],
      y: 0,
      height: '100%',
    },
  };

  const videosWithoutCategories = channelVideos.reduce((acc, video) => {
    return !video.category ? [...acc, video] : acc;
  }, [] as ChannelVideo[]);

  const theme = useMantineTheme();
  const isMd = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const isSm = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const showCaroussel = useCallback(
    (count: number) => (isSm && count > 1) || (isMd && count > 2) || count > 3,
    [isMd, isSm],
  );

  function Description() {
    return (
      <Text>
        Enquanto nosso aplicativo não está disponível para download, criamos esse canal. Por aqui,
        iremos nos comunicar antes de você ter acesso a todo acervo de conteúdos e funcionalidades
        que estamos desenvolvendo. Aguarde, em breve, você terá acesso a uma plataforma que irá te
        conectar ao mundo dos vinhos.
      </Text>
    );
  }

  return (
    <S.Wrapper bg="dark">
      <LandingPageBanner
        title={'Canal'}
        banner={'/assets/channel-banner.jpg'}
        description={<Description />}
        textAlignment="bottom-left"
        imgFit="contain"
        imgSize={887}
      />
      <S.Content>
        {!!categories.length && (
          <Flex direction="column" gap="xl">
            {categories.map((category) => (
              <Box key={category.id}>
                <Title mb="xl" fz={24} order={2}>
                  {category.name}
                </Title>
                {showCaroussel(category.videos?.length || 0) ? (
                  <ChannelVideoCarousel
                    slideSize="33.333333%"
                    slideGap={15}
                    mx="auto"
                    w="100%"
                    align="start"
                    controlsOffset={-20}
                    breakpoints={[
                      { maxWidth: 'md', slideSize: '50%' },
                      { maxWidth: 'sm', slideSize: '100%', slideGap: 0 },
                    ]}
                    controlSize={32}
                    styles={{
                      viewport: {
                        padding: '20px 0',
                      },
                    }}
                  >
                    {category.videos?.map((video) => (
                      <ChannelVideoCarouselSlide key={video.id}>
                        <motion.div variants={itemMotion} viewport={{ root: scrollRef }}>
                          <ChannelVideoCard
                            {...video}
                            gradientColor={category.color}
                            disableVertical={true}
                          />
                        </motion.div>
                      </ChannelVideoCarouselSlide>
                    ))}
                  </ChannelVideoCarousel>
                ) : (
                  <Grid>
                    {category.videos?.map((video) => (
                      <Grid.Col key={video.id} span={isSm ? 12 : isMd ? 6 : 4}>
                        <motion.div variants={itemMotion} viewport={{ root: scrollRef }}>
                          <ChannelVideoCard
                            {...video}
                            gradientColor={category.color}
                            disableVertical={true}
                          />
                        </motion.div>
                      </Grid.Col>
                    ))}
                  </Grid>
                )}
              </Box>
            ))}
          </Flex>
        )}
        {!!videosWithoutCategories.length && (
          <S.DataWrapper length={videosWithoutCategories.length}>
            {videosWithoutCategories.map((channelVideo) => (
              <motion.div
                key={channelVideo.id}
                initial="hidden"
                whileInView="show"
                variants={itemMotion}
                viewport={{ root: scrollRef, once: true }}
              >
                <ChannelVideoCard
                  key={channelVideo.id}
                  {...channelVideo}
                  gradientColor={channelVideo.category?.color}
                  disableVertical={true}
                />
              </motion.div>
            ))}
          </S.DataWrapper>
        )}
        {!categories.length && !videosWithoutCategories.length && (
          <p className="empty-message">Não temos nenhum vídeo disponível no momento</p>
        )}
      </S.Content>
    </S.Wrapper>
  );
};

export default ChannelVideoList;
